import React from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { GoogleReviews } from '../components/GoogleReviews';
import { ArrowRight, Calendar, MessageCircle, Microscope, Scan, MonitorPlay, ShieldCheck, Languages, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
export function AboutPage() {
  const {
    t,
    lang
  } = useTranslation();
  const seoTitle = lang === 'en' ? 'About Dentico Clinic | Private Dentist in Angel' : lang === 'ua' ? 'Про Dentico Clinic | Приватний стоматолог в Angel' : lang === 'ro' ? 'Despre Dentico Clinic | Dentist Privat în Angel' : 'О Dentico Clinic | Частный стоматолог в Angel';
  const seoDescription = lang === 'en' ? 'Learn about Dentico Clinic in Angel, London — a calm, modern private dental clinic focused on trust, clarity and patient care.' : lang === 'ua' ? 'Дізнайтеся про Dentico Clinic в Angel, Лондон — спокійну, сучасну приватну стоматологічну клініку, орієнтовану на довіру, ясність та турботу про пацієнтів.' : lang === 'ro' ? 'Aflați despre Dentico Clinic din Angel, Londra — o clinică dentară privată calmă și modernă, axată pe încredere, claritate și îngrijirea pacienților.' : 'Узнайте о Dentico Clinic в Angel, Лондон — спокойной, современной частной стоматологической клинике, ориентированной на доверие, ясность и заботу о пациентах.';
  const advantages = [{
    icon: Microscope,
    title: lang === 'en' ? 'Dental Microscope' : lang === 'ua' ? 'Стоматологічний мікроскоп' : lang === 'ro' ? 'Microscop Dentar' : 'Стоматологический микроскоп',
    desc: lang === 'en' ? 'Higher precision for root canals' : lang === 'ua' ? 'Вища точність лікування каналів' : lang === 'ro' ? 'Precizie mai mare pentru tratamentele de canal' : 'Высокая точность лечения каналов'
  }, {
    icon: Scan,
    title: lang === 'en' ? '3D Digital Scanner' : lang === 'ua' ? 'Цифровий 3D сканер' : lang === 'ro' ? 'Scanner Digital 3D' : 'Цифровой 3D сканер',
    desc: lang === 'en' ? 'No messy impressions needed' : lang === 'ua' ? 'Без відбитків — миттєвий результат' : lang === 'ro' ? 'Fără amprente dezordonate' : 'Без слепков — мгновенный результат'
  }, {
    icon: MonitorPlay,
    title: lang === 'en' ? 'Digital Smile Design' : lang === 'ua' ? 'Цифровий дизайн посмішки' : lang === 'ro' ? 'Design Digital al Zâmbetului' : 'Цифровой дизайн улыбки',
    desc: lang === 'en' ? 'See your result before treatment' : lang === 'ua' ? 'Побачте результат до лікування' : lang === 'ro' ? 'Vedeți rezultatul înainte de tratament' : 'Увидьте результат до лечения'
  }, {
    icon: ShieldCheck,
    title: lang === 'en' ? 'UK Sterilisation Standards' : lang === 'ua' ? 'Стандарти стерилізації UK' : lang === 'ro' ? 'Standarde de Sterilizare UK' : 'Стандарты стерилизации UK',
    desc: lang === 'en' ? 'HTM 01-05 — safety you can trust' : lang === 'ua' ? 'HTM 01-05 — безпека, якій довіряють' : lang === 'ro' ? 'HTM 01-05 — siguranță de încredere' : 'HTM 01-05 — безопасность, которой доверяют'
  }, {
    icon: Languages,
    title: lang === 'en' ? 'Multilingual Team' : lang === 'ua' ? 'Багатомовна команда' : lang === 'ro' ? 'Echipă Multilingvă' : 'Многоязычная команда',
    desc: lang === 'en' ? 'English · Ukrainian · Russian' : lang === 'ua' ? 'Українська · English · Російська' : lang === 'ro' ? 'Engleză · Ucraineană · Rusă' : 'Русский · English · Українська'
  }, {
    icon: CreditCard,
    title: lang === 'en' ? 'Transparent Pricing' : lang === 'ua' ? 'Прозорі ціни' : lang === 'ro' ? 'Prețuri Transparente' : 'Прозрачные цены',
    desc: lang === 'en' ? 'No hidden fees — ever' : lang === 'ua' ? 'Жодних прихованих платежів' : lang === 'ro' ? 'Fără taxe ascunse — niciodată' : 'Никаких скрытых платежей'
  }];
  return <Layout title={seoTitle} description={seoDescription} data-id="element-470">
      {/* Hero with image */}
      <section className="relative" data-id="element-471">
        <div className="h-[280px] sm:h-[360px] lg:h-[420px] w-full overflow-hidden" data-id="element-472">
          <img src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1600&q=80" alt="Premium Dentico Clinic Interior" className="w-full h-full object-cover" data-id="element-473" />
          <div className="absolute inset-0 bg-gradient-to-t from-forest-900/80 via-forest-900/40 to-forest-900/20" data-id="element-474" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12" data-id="element-475">
          <div className="max-w-7xl mx-auto text-center" data-id="element-476">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3" data-id="element-477">
              {t('about.title')}
            </h1>
            <p className="text-lg text-ivory/80 max-w-xl mx-auto" data-id="element-478">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section - shorter text */}
      <section className="py-16 lg:py-20 bg-ivory" data-id="element-479">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-480">
          <div className="grid md:grid-cols-2 gap-10 items-center" data-id="element-481">
            <div className="relative" data-id="element-482">
              <div className="absolute inset-0 bg-ivory-200 rounded-3xl transform -rotate-3" data-id="element-483"></div>
              <img src="https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=800&q=80" alt="Dentist consultation with patient" className="relative rounded-3xl shadow-xl w-full h-[320px] lg:h-[420px] object-cover" data-id="element-484" />
            </div>
            <div className="space-y-5" data-id="element-485">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-486">
                {t('about.storyTitle')}
              </h2>
              <p className="text-charcoal-light leading-relaxed" data-id="element-487">
                {t('about.storyP1')}
              </p>
              <p className="text-charcoal-light leading-relaxed" data-id="element-488">
                {t('about.storyP2')}
              </p>
              <div className="pt-2" data-id="element-489">
                <Link to={`/${lang}/contact`} data-id="element-490">
                  <Button size="lg" className="gap-2" data-id="element-491">
                    {t('about.meetTeam')} <ArrowRight className="w-4 h-4" data-id="element-492" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Advantages */}
      <section className="py-16 lg:py-20 bg-ivory-200" data-id="element-493">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-494">
          <div className="text-center mb-12" data-id="element-495">
            <h2 className="text-3xl font-bold text-charcoal mb-3" data-id="element-496">
              {lang === 'en' ? 'Advanced Technology & Care' : lang === 'ua' ? 'Передові технології та турбота' : lang === 'ro' ? 'Tehnologie Avansată și Îngrijire' : 'Передовые технологии и забота'}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-497">
            {advantages.map((adv, idx) => <div key={idx} className="bg-ivory p-5 lg:p-6 rounded-2xl border border-beige/60 flex flex-col items-center text-center" data-id="element-498">
                <div className="w-12 h-12 bg-ivory-200 rounded-full flex items-center justify-center text-forest-600 mb-4" data-id="element-499">
                  <adv.icon className="w-6 h-6" data-id="element-500" />
                </div>
                <h3 className="text-sm lg:text-base font-bold text-charcoal mb-1" data-id="element-501">
                  {adv.title}
                </h3>
                <p className="text-stone-500 text-xs lg:text-sm" data-id="element-502">{adv.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <GoogleReviews data-id="element-503" />

      {/* CTA */}
      <section className="py-16 bg-forest-900" data-id="element-504">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-505">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-6" data-id="element-506">
            {t('about.ctaTitle')}
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-507">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-508">
              <Button size="lg" className="gap-2 w-full sm:w-auto bg-ivory text-forest-900 hover:bg-stone-100" data-id="element-509">
                <Calendar className="w-5 h-5" data-id="element-510" />
                {t('nav.bookOnline')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-511">
              <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto border-forest-400 text-ivory hover:bg-forest-800 hover:text-white" data-id="element-512">
                <MessageCircle className="w-5 h-5" data-id="element-513" />
                WhatsApp
              </Button>
            </a>
            <Link to={`/${lang}/contact`} data-id="element-514">
              <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto border-forest-400 text-ivory hover:bg-forest-800 hover:text-white" data-id="element-515">
                {t('nav.contact')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>;
}